import { forwardRef } from 'react'
import moment from 'moment'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'

function parseValue(value, format) {
  if (!value) return null
  const parsed = moment(value, format, true)
  return parsed.isValid() ? parsed : null
}

function parseBoundary(value, format) {
  if (!value) return undefined
  const parsed = moment(value, format, true)
  return parsed.isValid() ? parsed : undefined
}

function createChangeEvent(name, value) {
  return { target: { name, value } }
}

function PickerInput({
  Picker,
  value,
  valueFormat,
  displayFormat,
  onChange,
  min,
  max,
  className,
  name,
  id,
  disabled,
  required,
  readOnly,
  autoFocus,
  error,
  inputProps,
  onBlur,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  ...pickerProps
}, ref) {
  const boundaryProps = Picker === DateTimePicker
    ? {
        minDateTime: parseBoundary(min, valueFormat),
        maxDateTime: parseBoundary(max, valueFormat),
      }
    : Picker === DatePicker
      ? {
          minDate: parseBoundary(min, valueFormat),
          maxDate: parseBoundary(max, valueFormat),
        }
      : {}
  const htmlInputProps = {
    ...inputProps,
    id,
    name,
    min,
    max,
    required,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Picker
        {...pickerProps}
        {...boundaryProps}
        disabled={disabled}
        readOnly={readOnly}
        format={displayFormat}
        value={parseValue(value, valueFormat)}
        onChange={(nextValue) => {
          const next = nextValue?.isValid() ? nextValue.format(valueFormat) : ''
          onChange?.(createChangeEvent(name, next))
        }}
        slotProps={{
          textField: {
            autoFocus,
            className,
            error: Boolean(error || ariaInvalid),
            fullWidth: true,
            inputRef: ref,
            onBlur,
            size: 'small',
            inputProps: htmlInputProps,
          },
        }}
      />
    </LocalizationProvider>
  )
}

const ForwardedPickerInput = forwardRef(PickerInput)

export const DatePickerInput = forwardRef(function DatePickerInput(props, ref) {
  return (
    <ForwardedPickerInput
      {...props}
      ref={ref}
      Picker={DatePicker}
      valueFormat="YYYY-MM-DD"
      displayFormat="YYYY-MM-DD"
    />
  )
})

export const DateTimePickerInput = forwardRef(function DateTimePickerInput(props, ref) {
  return (
    <ForwardedPickerInput
      {...props}
      ref={ref}
      Picker={DateTimePicker}
      valueFormat="YYYY-MM-DDTHH:mm"
      displayFormat="YYYY-MM-DD HH:mm"
      ampm={false}
    />
  )
})

export const TimePickerInput = forwardRef(function TimePickerInput(props, ref) {
  return (
    <ForwardedPickerInput
      {...props}
      ref={ref}
      Picker={TimePicker}
      valueFormat="HH:mm"
      displayFormat="HH:mm"
      ampm={false}
    />
  )
})
